import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Award, Target } from 'lucide-react';

interface SdrProgress {
  id: string;
  name: string;
  current: number;
  target: number;
  clients: string[];
  assignedClient?: string;
}

interface SdrProgressBarsProps {
  sdrs: SdrProgress[];
}

const SdrProgressBars: React.FC<SdrProgressBarsProps> = ({ sdrs }) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-teal-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return { bar: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' };
    if (percentage >= 75) return { bar: 'bg-cyan-500', text: 'text-cyan-700', bg: 'bg-cyan-50' };
    if (percentage >= 50) return { bar: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50' };
    return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' };
  };

  const sortedSdrs = [...sdrs].sort((a, b) => {
    const percentA = (a.current / a.target) * 100;
    const percentB = (b.current / b.target) * 100;
    return percentB - percentA;
  });

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-cyan-50 p-3 rounded-xl">
            <Trophy className="h-6 w-6 text-cyan-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Progression des SDRs</h3>
            <p className="text-sm text-gray-500">Objectifs individuels du jour</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 max-h-[calc(100vh-350px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {sortedSdrs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun SDR actif aujourd'hui</p>
          </div>
        ) : (
          sortedSdrs.map((sdr, index) => {
            const percentage = Math.min((sdr.current / sdr.target) * 100, 100);
            const colors = getProgressColor(percentage);

            return (
              <motion.div
                key={sdr.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3 w-48">
                    {index < 3 && (
                      <div className="flex-shrink-0">
                        {index === 0 && <Trophy className="h-5 w-5 text-yellow-500" />}
                        {index === 1 && <Award className="h-5 w-5 text-gray-400" />}
                        {index === 2 && <Award className="h-5 w-5 text-orange-600" />}
                      </div>
                    )}
                    <div
                      className={`${getAvatarColor(
                        sdr.name
                      )} h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm flex-shrink-0`}
                    >
                      {getInitials(sdr.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{sdr.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {sdr.assignedClient || (sdr.clients.length > 0 ? `${sdr.clients.length} client${sdr.clients.length > 1 ? 's' : ''}` : 'Sans client')}
                      </p>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {sdr.current} / {sdr.target}
                        </span>
                        <span className="text-xs text-gray-500">RDVs</span>
                      </div>
                      <div className={`${colors.bg} ${colors.text} px-2 py-1 rounded-lg text-xs font-bold`}>
                        {Math.round(percentage)}%
                      </div>
                    </div>

                    <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1, delay: index * 0.05 + 0.3 }}
                        className={`h-full ${colors.bar} rounded-full relative`}
                      >
                        {percentage > 20 && (
                          <div className="absolute inset-0 bg-white/20 animate-pulse" />
                        )}
                      </motion.div>
                    </div>
                  </div>

                  <div className="w-24 text-right">
                    {percentage >= 100 ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', delay: index * 0.05 + 0.5 }}
                        className="inline-flex items-center space-x-1 bg-green-100 text-green-700 px-3 py-1 rounded-lg"
                      >
                        <Trophy className="h-4 w-4" />
                        <span className="text-xs font-bold">Objectif!</span>
                      </motion.div>
                    ) : (
                      <span className="text-sm font-semibold text-gray-500">
                        -{sdr.target - sdr.current}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SdrProgressBars;
